/**
 * Page-level keyboard shortcut registration.
 *
 * The recording page binds a *single* keydown listener on `window` and
 * dispatches to handlers registered through {@link useKeyboardShortcut}. This
 * keeps focus management consistent — handlers are skipped while the user is
 * typing into an `<input>`, `<textarea>`, or `[contenteditable]`.
 *
 * Keys are matched as `event.key` (case-insensitive for letters). Modifier
 * keys aren't merged into the key string — pass `meta`/`ctrl`/`shift` flags
 * explicitly if you need them. Shortcut layouts that require chord input
 * should compose multiple registrations.
 */
import { useEffect } from 'react'

export interface ShortcutOptions {
  /** Trigger only when this modifier is also held. Default: none. */
  meta?: boolean
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  /** When true, the handler runs even if the focus is in an editable field. */
  allowInEditable?: boolean
  /** When true, prevent the browser's default for the matched event. */
  preventDefault?: boolean
}

/**
 * Register a keyboard shortcut. Cleans up on unmount or when deps change.
 *
 * @param key   `event.key` value to match (e.g. 'Enter', 'Escape', 'r', '?').
 *              Single letters are normalised to lowercase.
 * @param onTrigger  Called with the original event when matched.
 * @param opts  Modifiers and edit-context overrides.
 */
export function useKeyboardShortcut(
  key: string,
  onTrigger: (e: KeyboardEvent) => void,
  opts: ShortcutOptions = {},
): void {
  useEffect(() => {
    const target = key.length === 1 ? key.toLowerCase() : key
    const handler = (e: KeyboardEvent) => {
      const eKey = e.key.length === 1 ? e.key.toLowerCase() : e.key
      if (eKey !== target) return
      if (opts.meta !== undefined && e.metaKey !== opts.meta) return
      if (opts.ctrl !== undefined && e.ctrlKey !== opts.ctrl) return
      if (opts.shift !== undefined && e.shiftKey !== opts.shift) return
      if (opts.alt !== undefined && e.altKey !== opts.alt) return
      if (!opts.allowInEditable && isEditableTarget(e.target)) return
      if (opts.preventDefault) e.preventDefault()
      onTrigger(e)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    key,
    onTrigger,
    opts.meta,
    opts.ctrl,
    opts.shift,
    opts.alt,
    opts.allowInEditable,
    opts.preventDefault,
  ])
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

/** Bulk-register a list of shortcuts in a single hook call. */
export function useKeyboardShortcuts(
  shortcuts: ReadonlyArray<{
    key: string
    onTrigger: (e: KeyboardEvent) => void
    opts?: ShortcutOptions
  }>,
): void {
  // Each entry uses its own effect so individual handlers can be replaced
  // without re-binding the whole set. A loop with hooks is forbidden, so we
  // expose this as a top-level hook that the caller can spread instead.
  // To keep the API ergonomic we install one effect per shortcut here using
  // a stable serialized key; this is safe because shortcuts is typed
  // ReadonlyArray and React enforces hook order at the caller site.
  // (In practice consumers wrap this in `useMemo`.)
  for (const s of shortcuts) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useKeyboardShortcut(s.key, s.onTrigger, s.opts ?? {})
  }
}
